/**
 * "KÆRE HJÆLPEREN" — the composer's copy of the group-DM bot trigger.
 *
 * In a group DM the bot answers only a message that opens by addressing it:
 * `Kære <navn>`, case-insensitive, followed by nothing, whitespace or
 * punctuation (so "Kære Hjælperens venner" is not for Hjælperen, and a bot
 * called Bo does not answer a greeting to Bob). The server decides for real
 * in `backend/src/modules/llm-bot/address.ts`; this is the SAME rule so the
 * composer can say "Hjælperen svarer" before the message is sent. The two
 * projects share no source, so it is written twice — keep them in step.
 */

export interface AddressableBot {
  id: number;
  name: string;
}

const OPENING = /^kære\s+/u;
const NAME_BOUNDARY = /[\p{L}\p{N}]/u;

export interface AddressedBotMatch<T extends AddressableBot> {
  bot: T;
  /** Characters from the first non-whitespace one that make up "Kære <navn>". */
  length: number;
}

/** The bot the text opens by greeting, and how long the greeting is, or `null`. */
export function matchAddressedBot<T extends AddressableBot>(
  text: string,
  bots: readonly T[],
): AddressedBotMatch<T> | null {
  const lowered = text.trimStart().toLocaleLowerCase("da");
  const opening = OPENING.exec(lowered);
  if (!opening) return null;
  const rest = lowered.slice(opening[0].length);

  for (const bot of bots) {
    const name = bot.name.trim().toLocaleLowerCase("da");
    if (!name) continue;
    if (!rest.startsWith(name)) continue;
    const after = rest.charAt(name.length);
    if (after === "" || !NAME_BOUNDARY.test(after)) {
      return { bot, length: opening[0].length + name.length };
    }
  }
  return null;
}

/** The bot the text opens by greeting, or `null`. */
export function findAddressedBot<T extends AddressableBot>(
  text: string,
  bots: readonly T[],
): T | null {
  return matchAddressedBot(text, bots)?.bot ?? null;
}
